import React, { useState, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useFeatureService } from './services/useFeatureService';
import type { Feature, SystemEntityStatus } from './services/FeatureService';
import { EntityEditor } from './EntityEditor';
import { Button } from '../../Button/Button';
import { Input } from '../../Input/Input';
import { AlertDialog } from '../../AlertDialog/AlertDialog';
import { LayoutContent } from '../../LayoutContent';
import { useDynamicEntitiesContext, useDynamicEntitiesPermissions } from '../core/DynamicEntitiesProvider';
import {
    PencilSquareIcon,
    TrashIcon,
    Bars3Icon,
    PlusIcon,
    CheckIcon,
    LockClosedIcon,
    ChevronDownIcon,
    EyeIcon,
} from '@heroicons/react/24/outline';

interface FeatureEditorProps {
    featureId: string;
    onBack: () => void;
}

const GripIcon = () => (
    <div className="text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600 transition-colors p-1">
        <Bars3Icon className="w-5 h-5" />
    </div>
);

const SortableFeatureEntity = ({ fe, onEdit, onDelete, canDelete = true }: any) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: fe.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const isSystem = fe.dynamicEntity?.isSystem === true;

    return (
        <div ref={setNodeRef} style={style} className="flex items-center justify-between py-4 border-b border-gray-100 group bg-white hover:bg-gray-50/50 transition-colors px-2">
            <div className="flex items-center gap-4 flex-1">
                <div {...attributes} {...listeners} className="outline-none">
                    <GripIcon />
                </div>
                <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4 font-medium text-sm text-gray-900 flex items-center gap-1.5">
                        {isSystem && (
                            <LockClosedIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        )}
                        {fe.dynamicEntity?.code}
                    </div>
                    <div className="col-span-5 text-sm text-gray-500">
                        {fe.dynamicEntity?.description || fe.dynamicEntity?.code}
                    </div>
                    <div className="col-span-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-normal bg-[#dbeefe] text-[#0e79fd]">
                            {fe.renderMode === 'tab' ? 'Pestaña' : 'En Línea'}
                        </span>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 w-[100px] justify-center">
                {isSystem ? (
                    <Button
                        type="plain"
                        iconOnly
                        leftIcon={<EyeIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />}
                        ariaLabel="Ver campos"
                        onClick={() => onEdit(fe)}
                    />
                ) : (
                    <>
                        <Button
                            type="plain"
                            iconOnly
                            leftIcon={<PencilSquareIcon className="w-5 h-5 text-blue-500 hover:text-blue-700" />}
                            ariaLabel="Editar feature"
                            onClick={() => onEdit(fe)}
                        />
                        {canDelete && (
                            <Button
                                type="plain"
                                iconOnly
                                leftIcon={<TrashIcon className="w-5 h-5 text-red-500 hover:text-red-700" />}
                                ariaLabel="Eliminar feature"
                                onClick={() => onDelete(fe)}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export const FeatureEditor: React.FC<FeatureEditorProps> = ({ featureId, onBack }) => {
    const { onError } = useDynamicEntitiesContext();
    const { canCreate, canUpdate, canDelete } = useDynamicEntitiesPermissions();
    const service = useFeatureService();
    const [feature, setFeature] = useState<Feature | null>(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<'feature' | 'entity'>('feature');
    const [selectedFeatureEntity, setSelectedFeatureEntity] = useState<any | null>(null);

    // Modal State — add regular entity
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newEntityCode, setNewEntityCode] = useState('');
    const [newEntityDesc, setNewEntityDesc] = useState('');
    const [newEntityRenderMode, setNewEntityRenderMode] = useState<'tab' | 'inline'>('tab');

    // Order State
    const [isOrderDirty, setIsOrderDirty] = useState(false);

    // System entities state
    const [systemEntities, setSystemEntities] = useState<SystemEntityStatus[]>([]);
    const [systemEntitiesLoading, setSystemEntitiesLoading] = useState(false);
    const [isSystemPanelOpen, setIsSystemPanelOpen] = useState(false);

    // Enable modal state
    const [enableModalEntity, setEnableModalEntity] = useState<SystemEntityStatus | null>(null);
    const [enableRenderMode, setEnableRenderMode] = useState<'tab' | 'inline'>('tab');
    const [isEnabling, setIsEnabling] = useState(false);

    // Disable confirmation modal state
    const [disableModalEntity, setDisableModalEntity] = useState<SystemEntityStatus | null>(null);
    const [isDisabling, setIsDisabling] = useState(false);

    const loadFeature = () => {

        service.getById(featureId).then(f => {
            setFeature({
                ...f,
                featureEntities: f.featureEntities ? f.featureEntities.filter((fe: any) => !fe.dynamicEntity?.isPlansAndCriteria) : []
            });
            setLoading(false);
        }).catch(err => {
            console.error(err);
            onError?.(err as Error);
            setLoading(false);
        });
    };

    const loadSystemEntities = () => {
        setSystemEntitiesLoading(true);
        service.getSystemEntities(featureId).then(data => {
            setSystemEntities(data);
        }).catch(err => {
            console.error(err);
            onError?.(err as Error);
        }).finally(() => {
            setSystemEntitiesLoading(false);
        });
    };

    useEffect(() => {
        loadFeature();
        loadSystemEntities();
    }, [featureId, service]);

    // Handlers — regular entities
    const handleConfirmAddEntity = async () => {
        if (!newEntityCode.trim()) {
            alert('El código de la entidad es requerido');
            return;
        }

        try {
            setLoading(true);


            const entity = await service.createEntity({
                code: newEntityCode.trim(),
                description: newEntityDesc.trim() || newEntityCode.trim(),
                id: crypto.randomUUID(),
                multiRecord: false
            });

            await service.addFeatureEntity(featureId, {
                id: crypto.randomUUID(),
                featureId: featureId,
                dynamicEntityId: entity.id,
                renderMode: newEntityRenderMode,
                order: (feature?.featureEntities?.length || 0) + 1,
                properties: {}
            });

            setIsAddModalOpen(false);
            setNewEntityCode('');
            setNewEntityDesc('');
            setNewEntityRenderMode('tab');
            loadFeature();
        } catch (err) {
            console.error(err);
            onError?.(err as Error);
            alert('Error al crear la entidad');
            setLoading(false);
        }
    };

    const handleSaveOrder = async () => {
        if (!feature || !feature.featureEntities) return;

        setLoading(true);
        try {

            await Promise.all(feature.featureEntities.map((e: any) =>
                service.updateFeatureEntity(featureId, e.dynamicEntityId, e)
            ));
            setIsOrderDirty(false);
            loadFeature();
        } catch (err) {
            console.error(err);
            onError?.(err as Error);
            alert('Error al guardar el nuevo orden');
        } finally {
            setLoading(false);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!feature || !feature.featureEntities || !over) return;

        if (active.id !== over.id) {
            const entities = [...feature.featureEntities].sort((a, b) => (a.order || 0) - (b.order || 0));
            const oldIndex = entities.findIndex((e) => e.id === active.id);
            const newIndex = entities.findIndex((e) => e.id === over.id);

            const newEntities = arrayMove(entities, oldIndex, newIndex);
            const updatedEntities = newEntities.map((e, idx) => ({ ...e, order: idx }));

            setFeature({ ...feature, featureEntities: updatedEntities });
            setIsOrderDirty(true);
        }
    };

    const handleDeleteEntity = async (fe: any) => {
        if (!confirm('¿Está seguro? Esto elimina el vínculo, pero conserva la definición de la entidad.')) return;
        try {
            setLoading(true);

            await service.removeFeatureEntity(featureId, fe.dynamicEntityId);
            loadFeature();
        } catch (err) {
            console.error(err);
            onError?.(err as Error);
            setLoading(false);
        }
    };

    // Handlers — system entities
    const handleOpenEnableModal = (entity: SystemEntityStatus) => {
        setEnableRenderMode('tab');
        setEnableModalEntity(entity);
    };

    const handleConfirmEnable = async () => {
        if (!enableModalEntity) return;
        setIsEnabling(true);
        try {

            await service.enableSystemEntity(featureId, enableModalEntity.id, enableRenderMode);
            setEnableModalEntity(null);
            loadFeature();
            loadSystemEntities();
        } catch (err) {
            console.error(err);
            onError?.(err as Error);
            alert('Error al habilitar la entidad de sistema');
        } finally {
            setIsEnabling(false);
        }
    };

    const handleConfirmDisable = async () => {
        if (!disableModalEntity?.enabledSystemEntityId) return;
        setIsDisabling(true);
        try {

            await service.disableSystemEntity(featureId, disableModalEntity.enabledSystemEntityId);
            setDisableModalEntity(null);
            loadFeature();
            loadSystemEntities();
        } catch (err) {
            console.error(err);
            onError?.(err as Error);
            alert('Error al deshabilitar la entidad de sistema');
        } finally {
            setIsDisabling(false);
        }
    };

    if (loading && !feature) return <div className="p-6 text-gray-500">Cargando maestro...</div>;
    if (!feature) return <div className="p-6 text-red-500">Maestro no encontrado</div>;

    if (view === 'entity' && selectedFeatureEntity) {
        return (
            <div className="w-full h-full">
                <EntityEditor
                    entityId={selectedFeatureEntity.dynamicEntityId}
                    featureId={featureId}
                    featureEntityId={selectedFeatureEntity.id}
                    initialRenderMode={selectedFeatureEntity.renderMode || 'tab'}
                    onBack={() => {
                        setView('feature');
                        setSelectedFeatureEntity(null);
                        loadFeature();
                    }}
                />
            </div>
        );
    }

    const sortedEntities = feature.featureEntities
        ? [...feature.featureEntities].sort((a, b) => (a.order || 0) - (b.order || 0))
        : [];

    const enabledSystemCount = systemEntities.filter(e => e.isEnabled).length;

    const customToolbarActions = (
        <div className="flex gap-3">
            {isOrderDirty && canUpdate && (
                <Button
                    type="outline"
                    size="sm"
                    onClick={handleSaveOrder}
                    disabled={loading}
                    leftIcon={<CheckIcon className="w-4 h-4" />}
                >
                    Guardar Orden
                </Button>
            )}
            {canCreate && (
                <Button
                    type="default"
                    size="sm"
                    onClick={() => setIsAddModalOpen(true)}
                    leftIcon={<PlusIcon className="w-4 h-4" />}
                >
                    Nueva Entidad
                </Button>
            )}
        </div>
    );

    return (
        <LayoutContent
            title={`Opción - ${feature.name || feature.description || feature.code}`}
            onBack={onBack}
            toolbarEndContent={customToolbarActions}
        >
            <div className="flex flex-col w-full relative space-y-6">

                {/* Zona B — Lista principal de entidades */}
                <div className="bg-white rounded-2xl w-full p-6">
                    <div className="w-full">
                        <div className="flex w-full border-b border-gray-200 pb-3 mb-2 px-2">
                            <div className="w-9"></div>
                            <div className="flex-1 grid grid-cols-12 gap-4">
                                <div className="col-span-4 text-sm font-medium text-gray-400">Código</div>
                                <div className="col-span-5 text-sm font-medium text-gray-400">Nombre</div>
                                <div className="col-span-3 text-sm font-medium text-gray-400">Visualización</div>
                            </div>
                            <div className="w-[100px] text-center text-sm font-medium text-gray-400">Acciones</div>
                        </div>

                        {sortedEntities.length === 0 && (
                            <div className="text-center py-12 text-sm text-gray-400 border-b border-gray-100">
                                No hay entidades vinculadas. Haz clic en "Nueva Entidad" para empezar.
                            </div>
                        )}

                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={sortedEntities.map(e => e.id)} strategy={verticalListSortingStrategy}>
                                {sortedEntities.map((fe: any) => (
                                    <SortableFeatureEntity
                                        key={fe.id}
                                        fe={fe}
                                        onEdit={(feToEdit: any) => { setSelectedFeatureEntity(feToEdit); setView('entity'); }}
                                        onDelete={handleDeleteEntity}
                                        canDelete={canDelete}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>
                </div>

                {/* Zona A — Entidades de Sistema (acordeón) */}
                <div className="bg-white rounded-2xl w-full overflow-hidden">
                    <button
                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
                        onClick={() => setIsSystemPanelOpen(!isSystemPanelOpen)}
                    >
                        <div className="flex items-center gap-2">
                            <LockClosedIcon className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-semibold text-gray-700">Entidades de Sistema</span>
                            {enabledSystemCount > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    {enabledSystemCount} habilitada{enabledSystemCount !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                        <ChevronDownIcon
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isSystemPanelOpen ? 'rotate-180' : ''}`}
                        />
                    </button>

                    {isSystemPanelOpen && (
                        <div className="border-t border-gray-100">
                            {systemEntitiesLoading ? (
                                <div className="px-6 py-8 text-center text-sm text-gray-400">
                                    Cargando entidades de sistema...
                                </div>
                            ) : systemEntities.length === 0 ? (
                                <div className="px-6 py-8 text-center text-sm text-gray-400">
                                    No hay entidades de sistema disponibles.
                                </div>
                            ) : (
                                <>
                                    {/* Header */}
                                    <div className="flex w-full border-b border-gray-100 px-6 py-2">
                                        <div className="flex-1 grid grid-cols-12 gap-4">
                                            <div className="col-span-3 text-xs font-medium text-gray-400">Código</div>
                                            <div className="col-span-3 text-xs font-medium text-gray-400">Nombre</div>
                                            <div className="col-span-3 text-xs font-medium text-gray-400">Estado</div>
                                            <div className="col-span-3 text-xs font-medium text-gray-400">Visualización</div>
                                        </div>
                                        <div className="w-[110px] text-center text-xs font-medium text-gray-400">Acción</div>
                                    </div>

                                    {/* Rows */}
                                    {systemEntities.map(entity => {
                                        const enabledAtFormatted = entity.enabledAt
                                            ? new Date(entity.enabledAt).toLocaleString('es-CO', {
                                                day: '2-digit', month: 'short', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit'
                                            })
                                            : null;
                                        const enabledByShort = entity.enabledBy
                                            ? entity.enabledBy.slice(0, 8) + '…'
                                            : null;

                                        return (
                                            <div
                                                key={entity.id}
                                                className="flex items-center justify-between py-4 border-b border-gray-50 px-6 hover:bg-gray-50/40 transition-colors"
                                            >
                                                <div className="flex-1 grid grid-cols-12 gap-4 items-start">
                                                    <div className="col-span-3 text-sm font-medium text-gray-900 pt-0.5">
                                                        {entity.code}
                                                    </div>
                                                    <div className="col-span-3 text-sm text-gray-500 pt-0.5">
                                                        {entity.description || entity.code}
                                                    </div>
                                                    <div className="col-span-3 flex flex-col gap-1">
                                                        {entity.isEnabled ? (
                                                            <>
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 w-fit">
                                                                    Habilitada
                                                                </span>
                                                                {enabledAtFormatted && (
                                                                    <span className="text-xs text-gray-400" title={entity.enabledAt}>
                                                                        {enabledAtFormatted}
                                                                    </span>
                                                                )}
                                                                {enabledByShort && (
                                                                    <span className="text-xs text-gray-400" title={entity.enabledBy}>
                                                                        Por: {enabledByShort}
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 w-fit">
                                                                No habilitada
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="col-span-3 text-sm text-gray-400 pt-0.5">
                                                        {entity.isEnabled && entity.renderMode
                                                            ? (entity.renderMode === 'tab' ? 'Pestaña' : 'En Línea')
                                                            : '—'
                                                        }
                                                    </div>
                                                </div>
                                                <div className="w-[110px] flex justify-center shrink-0">
                                                    {entity.isEnabled ? (
                                                        canDelete && (
                                                            <Button
                                                                type="plain"
                                                                size="xs"
                                                                onClick={() => setDisableModalEntity(entity)}
                                                            >
                                                                Deshabilitar
                                                            </Button>
                                                        )
                                                    ) : (
                                                        canCreate && (
                                                            <Button
                                                                type="outline"
                                                                size="xs"
                                                                onClick={() => handleOpenEnableModal(entity)}
                                                            >
                                                                Habilitar
                                                            </Button>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    )}
                </div>

            </div>

            {/* Modal — Nueva Entidad */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden relative">
                        <div className="absolute right-4 top-4">
                            <Button type="plain" size="xs" iconOnly leftIcon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>} onClick={() => setIsAddModalOpen(false)} ariaLabel="Cerrar modal" />
                        </div>

                        <div className="px-6 py-5 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900">Nueva Entidad</h3>
                        </div>

                        <div className="p-6 space-y-5">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-900 flex gap-1">Código de Entidad <span className="text-red-300">*</span></label>
                                <Input
                                    type="text"
                                    placeholder="Ej: DATOS_ADICIONALES"
                                    value={newEntityCode}
                                    onChange={(e) => setNewEntityCode(e.target.value)}
                                    autoFocus
                                    inputSize="sm"
                                />
                                <p className="text-xs text-gray-500">Un identificador único sin espacios para la base de datos.</p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-900 flex gap-1">Nombre <span className="text-red-300">*</span></label>
                                <Input
                                    type="text"
                                    placeholder="Ej: Información técnica extra"
                                    value={newEntityDesc}
                                    onChange={(e) => setNewEntityDesc(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleConfirmAddEntity();
                                    }}
                                    inputSize="sm"
                                />
                                <p className="text-xs text-gray-500">El nombre visible que se mostrará en las pestañas.</p>
                            </div>

                            <div className="space-y-3 pt-2">
                                <label className="text-sm font-medium text-gray-900">Modo de Visualización</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className={`cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-colors ${newEntityRenderMode === 'tab' ? 'bg-[#dbeefe]/30 border-[#0e79fd]' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                        <input type="radio" name="renderMode" value="tab" className="sr-only" checked={newEntityRenderMode === 'tab'} onChange={() => setNewEntityRenderMode('tab')} />
                                        <span className={`text-sm font-medium ${newEntityRenderMode === 'tab' ? 'text-[#0e79fd]' : 'text-gray-700'}`}>Pestaña (Tab)</span>
                                    </label>
                                    <label className={`cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-colors ${newEntityRenderMode === 'inline' ? 'bg-[#dbeefe]/30 border-[#0e79fd]' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                        <input type="radio" name="renderMode" value="inline" className="sr-only" checked={newEntityRenderMode === 'inline'} onChange={() => setNewEntityRenderMode('inline')} />
                                        <span className={`text-sm font-medium ${newEntityRenderMode === 'inline' ? 'text-[#0e79fd]' : 'text-gray-700'}`}>En Línea</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                            <Button type="plain" size="sm" onClick={() => setIsAddModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="default" size="sm" onClick={handleConfirmAddEntity} disabled={!newEntityCode.trim() || loading}>
                                {loading ? 'Creando...' : 'Guardar'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal — Habilitar entidad de sistema */}
            {enableModalEntity && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                    <AlertDialog
                        title={`Habilitar "${enableModalEntity.description || enableModalEntity.code}"`}
                        onCancel={() => { setEnableModalEntity(null); setEnableRenderMode('tab'); }}
                        onConfirm={handleConfirmEnable}
                        confirmText="Habilitar"
                        cancelText="Cancelar"
                        isProcess={isEnabling}
                    >
                        <p className="text-sm leading-5 font-normal text-content-tertiary dark:text-content-tertiary">
                            Selecciona el modo de visualización para esta entidad en el Feature.
                        </p>
                        <div className="grid grid-cols-2 gap-3 pt-1">
                            <label className={`cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-colors ${enableRenderMode === 'tab' ? 'bg-[#dbeefe]/30 border-[#0e79fd]' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                <input
                                    type="radio"
                                    name="enableRenderMode"
                                    value="tab"
                                    className="sr-only"
                                    checked={enableRenderMode === 'tab'}
                                    onChange={() => setEnableRenderMode('tab')}
                                />
                                <span className={`text-sm font-medium ${enableRenderMode === 'tab' ? 'text-[#0e79fd]' : 'text-gray-700'}`}>
                                    Pestaña (Tab)
                                </span>
                            </label>
                            <label className={`cursor-pointer border rounded-lg p-3 flex flex-col items-center gap-2 transition-colors ${enableRenderMode === 'inline' ? 'bg-[#dbeefe]/30 border-[#0e79fd]' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                <input
                                    type="radio"
                                    name="enableRenderMode"
                                    value="inline"
                                    className="sr-only"
                                    checked={enableRenderMode === 'inline'}
                                    onChange={() => setEnableRenderMode('inline')}
                                />
                                <span className={`text-sm font-medium ${enableRenderMode === 'inline' ? 'text-[#0e79fd]' : 'text-gray-700'}`}>
                                    En Línea
                                </span>
                            </label>
                        </div>
                    </AlertDialog>
                </div>
            )}

            {/* Modal — Deshabilitar entidad de sistema */}
            {disableModalEntity && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                    <AlertDialog
                        title={`¿Deshabilitar "${disableModalEntity.description || disableModalEntity.code}"?`}
                        onCancel={() => setDisableModalEntity(null)}
                        onConfirm={handleConfirmDisable}
                        confirmText="Deshabilitar"
                        cancelText="Cancelar"
                        isProcess={isDisabling}
                    >
                        <p className="text-sm leading-5 font-normal text-content-tertiary dark:text-content-tertiary">
                            Esta acción eliminará la asociación con el Feature. Los datos EAV capturados no se eliminan.
                        </p>
                    </AlertDialog>
                </div>
            )}

        </LayoutContent>
    );
};
